import React, { useState, useEffect } from 'react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRangePicker } from 'react-date-range';
import enUSLocale from 'date-fns/locale/en-US';
import ptBRLocale from 'date-fns/locale/pt';
import esLocale from 'date-fns/locale/es';import styled from 'styled-components';
import { useTranslation } from "react-i18next";
import * as Vlab from 'vlab-frontend-components'

import {
    addDays,
    isSameDay,
    startOfDay,
    startOfMonth
} from "date-fns";

export interface ICalendarPicker {
    startDate: Date,
    endDate: Date,
    key: string
}

interface CalendarPickerProps {
    getDate: (date: ICalendarPicker) => void
    onApplyButtonClick?: () => void
    range: ICalendarPicker[]
}

export const DatePicker = styled(DateRangePicker)`
    position: relative;
    z-index: 99999;
    border-top: 1px solid #B7C6D1;

    
    /* .rdrDateDisplayWrapper, .rdrNextPrevButton, .rdrMonthName {
        display: none;
    } */

    .rdrDayToday .rdrDayNumber span::after {
        display: none !important;
    }

    .rdrMonths {
        pointer-events: auto;
    }

    .rdrDefinedRangesWrapper {
        width: 150px;
    }

    .rdrDateDisplayWrapper {
        background-color: #F7F8FC;
    }
    .rdrDateDisplayItem {
        border-radius: 8px;
    }

    @media (max-width: 1024px) {
        .rdrDateRangeWrapper {
            width: 220px;
        }

        .rdrDefinedRangesWrapper {
            width: 100px;
        }
    
        .rdrStaticRange {
            font-size: 12px;
            width: 100px;
        }
    }
`;

export default function Calendar({ getDate, onApplyButtonClick, range }: CalendarPickerProps) {
    const now = Date.now();
    const startOfToday = startOfDay(addDays(now, -1));
    const minDate = new Date(2023, 9, 1);
    const { t, i18n } = useTranslation();
    const [areDatesSelected, setAreDatesSelected] = useState(false); // Novo estado
    const [state, setState] = useState({
        selection: {
            startDate: addDays(new Date(), -1),
            endDate: addDays(new Date(), -1),
            key: 'selection'
        },
    });

    useEffect(() => {
        if (range && range[0]) {
            setState(prevState => ({
                ...prevState,
                selection: {
                    startDate: range[0].startDate,
                    endDate: range[0].endDate,
                    key: 'selection'
                },
            }));
            setAreDatesSelected(true);
        }
    }, [range]);

    const handleDateChange = (item: any) => {
        if (item.selection.startDate && item.selection.endDate) {
            const startDate = item.selection.startDate;
            const endDate = item.selection.endDate;
    
            setState(prevState => ({
                ...prevState,
                selection: {
                    ...prevState.selection,
                    startDate,
                    endDate,
                },
            }));
            
            setAreDatesSelected(true);
        } else {
            setAreDatesSelected(false);
        }
    };

    const getCalendarLocale = () => {
        let selectedLocale = enUSLocale;

        if (i18n.language === 'pt-BR') {
            selectedLocale = ptBRLocale;
        } else if (i18n.language === 'es') {
            selectedLocale = esLocale;
        } else {
            selectedLocale = enUSLocale;
        }
        return selectedLocale
    }
    
      return (
        <div style={{ backgroundColor: '#FFF',  display: 'flex', flexDirection: 'column', border: '1px solid #B7C6D1', borderTop: 'none'}}>
            <DatePicker
                onChange={handleDateChange}
                months={1}
                minDate={minDate}
                maxDate={startOfToday}
                direction="vertical"
                ranges={[state.selection]}
                locale={getCalendarLocale()}
                editableDateInputs
                inputRanges={[]}
                staticRanges={[
                    {
                        label: t('components.calendar.thisMonth'),
                        range: () => ({
                            startDate: startOfMonth(startOfToday),
                            endDate: startOfToday
                        }),
                        isSelected(range: any) {
                            const definedRange: any = this.range();
                            return (
                                isSameDay(range.startDate, definedRange.startDate) &&
                                isSameDay(range.endDate, definedRange.endDate)
                            );
                        }
                    },
                    {
                        label: t('components.calendar.lastThirtyDays'),
                        range: () => ({
                            startDate: addDays(startOfToday, -29),
                            endDate: startOfToday
                        }),
                        isSelected(range: any) {
                            const definedRange: any = this.range();
                            return (
                                isSameDay(range.startDate, definedRange.startDate) &&
                                isSameDay(range.endDate, definedRange.endDate)
                            );
                        }
                    },
                    {
                        label: t('components.calendar.lastFifteenDays'),
                        range: () => ({
                            startDate: addDays(startOfToday, -14),
                            endDate: startOfToday
                        }),
                        isSelected(range: any) {
                            const definedRange: any = this.range();
                            return (
                                isSameDay(range.startDate, definedRange.startDate) &&
                                isSameDay(range.endDate, definedRange.endDate)
                            );
                        }
                    },
                    {
                        label: t('components.calendar.yesterday'),
                        range: () => ({
                            startDate: startOfToday,
                            endDate: startOfToday
                        }),
                        isSelected(range: any) {
                            const definedRange: any = this.range();
                                return (
                                isSameDay(range.startDate, definedRange.startDate) &&
                                isSameDay(range.endDate, definedRange.endDate)
                            );
                        }
                    }
                ]}
            />

            <div className="flex items-center justify-end h-16 relative z-50 gap-4">
                <div className='w-[333px] h-full border-l border-solid border-eff2f7 flex justify-center items-center gap-4 p-4'>
                    <Vlab.Button 
                        variant='tertiary' 
                        className="z-50 w-full"
                        disabled={!areDatesSelected}
                        onClick={() => {
                            setState({
                                selection: {
                                    startDate: startOfToday,
                                    endDate: startOfToday,
                                    key: 'selection',
                                },
                            });
                            setAreDatesSelected(false);
                        }} 
                    >
                        {t('components.calendar.clear')}
                    </Vlab.Button>
                    
                    <Vlab.Button 
                        variant="primary"     
                        className="z-50 w-full"
                        disabled={!areDatesSelected}        
                        onClick={() => {
                            if (onApplyButtonClick) {
                                getDate(state.selection);
                                onApplyButtonClick(); 
                            }
                        }}  
                    >
                        {t('components.calendar.apply')}
                    </Vlab.Button>
                </div>
            </div>
        </div>
    )
}